// Auth-flow helpers shared by the sign-in UI.

// Client-side floor for new passwords (OWASP ASVS 5.0 V6.2.1 requires >= 8
// and recommends 15). The server-side Firebase password policy is the real
// enforcement point; this only gives early feedback.
export const MIN_PASSWORD_LENGTH = 12;

// Every credential failure maps to one message so the UI doesn't reveal
// whether an email is registered. Pair with Email Enumeration Protection in
// the Firebase console, which does the same at the API.
const GENERIC_SIGN_IN = "Incorrect email or password.";

const MESSAGES = {
  "auth/invalid-credential": GENERIC_SIGN_IN,
  "auth/invalid-login-credentials": GENERIC_SIGN_IN,
  "auth/user-not-found": GENERIC_SIGN_IN,
  "auth/wrong-password": GENERIC_SIGN_IN,
  "auth/user-disabled": GENERIC_SIGN_IN,
  "auth/invalid-email": "Enter a valid email address.",
  "auth/missing-email": "Enter a valid email address.",
  "auth/missing-password": "Enter your password.",
  "auth/weak-password": `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  "auth/password-does-not-meet-requirements":
    "That password doesn't meet the requirements. Try a longer one.",
  "auth/email-already-in-use": "An account with this email already exists. Try logging in instead.",
  "auth/account-exists-with-different-credential":
    "This email is registered with a different sign-in method. Log in with that method instead.",
  "auth/too-many-requests": "Too many attempts. Please wait a few minutes and try again.",
  "auth/network-request-failed": "Network error. Check your connection and try again.",
  "auth/popup-blocked": "Your browser blocked the sign-in pop-up. Allow pop-ups for this site and try again.",
};

// The user dismissing the Google pop-up isn't an error worth showing.
const SILENT = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
]);

// Returns the message to show for a Firebase Auth error, or null for none.
// Never surfaces raw SDK messages, which embed internal error codes.
export function authErrorMessage(error) {
  const code = error?.code;
  if (SILENT.has(code)) return null;
  return MESSAGES[code] || "Something went wrong. Please try again.";
}

// Validates a post-login return path: only same-origin app paths, never
// protocol-relative ("//evil.com") or backslash variants browsers normalise
// to them.
export function safeRedirectPath(path, fallback = "/portfolio") {
  if (typeof path !== "string" || !path.startsWith("/")) return fallback;
  if (path.startsWith("//") || path.includes("\\")) return fallback;
  return path;
}
